import ApiClient from "@/api/ApiClient";
import _ from "lodash";
import { getOperators } from "../api/operators";

type WgCache = {
  customers: WgCacheEntry<CustomerCache>;
  subscribers: WgCacheEntry<SubscriberCache>;
  reqs: WgCacheEntry<ReqCache>;
};

type CentralCache = {
  streets: CentralCacheEntry<StreetCache>;
  taxis: CentralCacheEntry<TaxiCache>;
  stops: CentralCacheEntry<StopCache>;
  zones: CentralCacheEntry<ZoneCache>;
};

type CallCenterCache = {
  operators: CallCenterCacheEntry<OperatorCache>;
};

type WgCacheEntry<T> = {
  [wgId: number]: {
    [id: number]: T;
  };
};

type CallCenterCacheEntry<T> = {
  [ccId: number]: {
    [id: number]: T;
  };
};

type CentralCacheEntry<T> = {
  [wgId: number]: {
    [centralId: number]: {
      [id: number]: T;
    };
  };
};

type StreetCache = {
  id: number;
  display: string;
};

type CustomerCache = {
  id: number;
  name: string;
  phone: string;
};

type SubscriberUserCache = {
  id: number;
  name: string;
  type: string;
};

type SubscriberCache = {
  id: number;
  name: string;
  ref: string;
  users: Record<number, SubscriberUserCache>;
};

type OperatorCache = {
  id: number;
  name: string;
};

type ReqCache = {
  id: number;
  nameClients: string;
};

type TaxiCache = {
  id: number;
  license: string;
};

type StopCache = {
  id: number;
  name: string;
};

type ZoneCache = {
  id: number;
  name: string;
};

const wgCached: WgCache = {
  customers: {},
  subscribers: {},
  reqs: {},
};

const centralCached: CentralCache = {
  streets: {},
  taxis: {},
  stops: {},
  zones: {},
};

const callCenterCached: CallCenterCache = {
  operators: {},
};

export const addCustomer = (wgId: number, data: CustomerCache) => {
  if (!wgCached.customers[wgId]) {
    wgCached.customers[wgId] = {};
  }
  wgCached.customers[wgId][data.id] = _.pick(data, ["id", "name", "phone"]);
};

const loadCentralResource = async (type: keyof CentralCache, wgId: number, centralId: number) => {
  switch (type) {
    case "streets":
      return await loadStreets(wgId, centralId);
    case "taxis":
      return await loadTaxis(wgId, centralId);
    case "stops":
      return await loadStops(wgId, centralId);
    case "zones":
      return await loadZones(wgId, centralId);
  }
};

const loadWgResource = async (type: keyof WgCache, wgId: number) => {
  switch (type) {
    case "reqs":
      return await loadReqs(wgId);
    case "subscribers":
      return await loadSubscribers(wgId);
    case "customers":
      return; // No-op, we add the customers to the cache manually
  }
};

const loadCallCenterResource = async (type: keyof CallCenterCache, ccId: number) => {
  switch (type) {
    case "operators":
      return await loadOperators(ccId);
  }
};

type StreetView = {
  id: number;
  display: string;
  idWorkgroup: number;
  idCentral: number;
  poi: number;
  deleted: boolean;
};

const loadStreets = async (wgId: number, centralId: number) => {
  const streets = await ApiClient().get<StreetView[]>(`/streets/streetView`, {
    params: {
      wgId,
      centralId,
    },
  });
  if (!centralCached.streets[wgId]) {
    centralCached.streets[wgId] = {};
  }
  centralCached.streets[wgId][centralId] = streets.data.reduce(
    (acc: Record<number, StreetCache>, street) => {
      acc[street.id] = _.pick(street, ["id", "display"]);
      return acc;
    },
    {}
  );
};

type TaxiView = {
  id: number;
  license: string;
  // Has more fields, but we only need these two
};

const loadTaxis = async (wgId: number, centralId: number) => {
  const taxis = await ApiClient().get<TaxiView[]>(`/taxis`, {
    params: {
      wgId,
      centralId,
    },
  });
  if (!centralCached.taxis[wgId]) {
    centralCached.taxis[wgId] = {};
  }
  centralCached.taxis[wgId][centralId] = taxis.data.reduce(
    (acc: Record<number, TaxiCache>, taxi) => {
      acc[taxi.id] = _.pick(taxi, ["id", "license"]);
      return acc;
    },
    {}
  );
};

const loadStops = async (wgId: number, centralId: number) => {
  const stops = await ApiClient().get<StopView[]>(`/stops`, {
    params: {
      wgId,
      centralId,
    },
  });

  if (!centralCached.stops[wgId]) {
    centralCached.stops[wgId] = {};
  }
  centralCached.stops[wgId][centralId] = stops.data.reduce(
    (acc: Record<number, StopCache>, stop) => {
      acc[stop.id] = _.pick(stop, ["id", "name"]);
      return acc;
    },
    {}
  );
};

const loadZones = async (wgId: number, centralId: number) => {
  const zones = await ApiClient().get<ZoneView[]>(`/zones`, {
    params: {
      wgId,
      centralId,
    },
  });

  if (!centralCached.zones[wgId]) {
    centralCached.zones[wgId] = {};
  }
  centralCached.zones[wgId][centralId] = zones.data.reduce(
    (acc: Record<number, ZoneCache>, zone) => {
      acc[zone.id] = _.pick(zone, ["id", "name"]);
      return acc;
    },
    {}
  );
};

type SubscriberView = {
  id: number;
  name: string;
  ref: string;
  users: { id: number; name: string; type: string }[];
  // Has more fields, but we only need these
};

const loadOperators = async (ccId: number) => {
  const operators = await getOperators();
  if (!callCenterCached.operators[ccId]) {
    callCenterCached.operators[ccId] = {};
  }
  callCenterCached.operators[ccId] = operators.reduce(
    (acc: Record<number, OperatorCache>, operator) => {
      acc[operator.id] = _.pick(operator, ["id", "name"]);
      return acc;
    },
    {}
  );
};

const loadSubscribers = async (wgId: number) => {
  const subscribers = await ApiClient().get<SubscriberView[]>(`/subscribers`, {
    params: {
      wgId,
    },
  });
  if (!wgCached.subscribers[wgId]) {
    wgCached.subscribers[wgId] = {};
  }
  wgCached.subscribers[wgId] = subscribers.data.reduce(
    (acc: Record<number, SubscriberCache>, subscriber) => {
      const sub: SubscriberCache = {
        ..._.pick(subscriber, ["id", "name", "ref"]),
        users: subscriber.users.reduce(
          (users: Record<number, { id: number; name: string; type: string }>, u) => {
            users[u.id] = _.pick(u, ["id", "name", "type"]);
            return users;
          },
          {}
        ),
      };
      acc[subscriber.id] = sub;
      return acc;
    },
    {}
  );
};

type ReqView = {
  id: number;
  nameClients: string;
};

const loadReqs = async (wgId: number) => {
  const reqs = await ApiClient().get<ReqView[]>(`/reqs`, {
    params: {
      wgId,
    },
  });
  wgCached.reqs[wgId] = reqs.data.reduce((acc: Record<number, ReqCache>, req: ReqCache) => {
    acc[req.id] = _.pick(req, ["id", "nameClients"]);
    return acc;
  }, {});
};

export async function getStreet(wgId: number, centralId: number, id: number) {
  return getCentralResource<StreetCache>("streets", wgId, centralId, id);
}

export async function getCustomer(wgId: number, id: number) {
  return wgCached.customers[wgId]?.[id] || "Customer not found";
}

export async function getSubscriber(wgId: number, id: number) {
  return getWgResource<SubscriberCache>("subscribers", wgId, id);
}

export async function getSubscriberUser(wgId: number, id: number) {
  if (!wgCached.subscribers[wgId]) {
    await loadSubscribers(wgId);
  }

  const user = Object.values(wgCached.subscribers[wgId])
    .flatMap((s) => Object.values(s.users))
    .find((u) => u.id === id);

  if (!user) {
    await loadSubscribers(wgId);
  }

  return Object.values(wgCached.subscribers[wgId])
    .flatMap((s) => Object.values(s.users))
    .find((u) => u.id === id) as SubscriberUserCache;
}

export async function getReq(wgId: number, id: number) {
  return getWgResource<ReqCache>("reqs", wgId, id);
}

export async function getTaxi(wgId: number, centralId: number, id: number) {
  return getCentralResource<TaxiCache>("taxis", wgId, centralId, id);
}

export async function getStop(wgId: number, centralId: number, id: number) {
  return getCentralResource<StopCache>("stops", wgId, centralId, id);
}

export async function getZone(wgId: number, centralId: number, id: number) {
  return getCentralResource<ZoneCache>("zones", wgId, centralId, id);
}

export async function getOperator(ccId: number, id: number) {
  return getCallCenterResource<OperatorCache>("operators", ccId, id);
}

async function getCentralResource<T>(
  type: keyof CentralCache,
  wgId: number,
  centralId: number,
  id: number
): Promise<T> {
  if (!centralCached[type][wgId]) {
    centralCached[type][wgId] = {};
  }

  if (!centralCached[type][wgId][centralId]) {
    await loadCentralResource(type, wgId, centralId);
  }

  let resource = centralCached[type][wgId][centralId]?.[id];
  if (!resource) {
    await loadCentralResource(type, wgId, centralId);
    resource = centralCached[type][wgId][centralId]?.[id] || "Resource not found";
  }

  return resource as T;
}

async function getWgResource<T>(type: keyof WgCache, wgId: number, id: number): Promise<T> {
  if (!wgCached[type][wgId]) {
    wgCached[type][wgId] = {};
  }

  let resource = wgCached[type][wgId]?.[id];
  if (!resource) {
    await loadWgResource(type, wgId);
    resource = wgCached[type][wgId]?.[id] || "Resource not found";
  }

  return resource as T;
}

async function getCallCenterResource<T>(
  type: keyof CallCenterCache,
  ccId: number,
  id: number
): Promise<T> {
  if (!callCenterCached[type][ccId]) {
    callCenterCached[type][ccId] = {};
  }

  let resource = callCenterCached[type][ccId]?.[id];
  if (!resource) {
    await loadCallCenterResource(type, ccId);
    resource = callCenterCached[type][ccId]?.[id] || "Resource not found";
  }

  return resource as T;
}

export default {
  getStreet,
  getCustomer,
  getSubscriber,
  getSubscriberUser,
  getReq,
  getTaxi,
  getStop,
  getZone,
  addCustomer,
};
